import React, { useMemo, useState } from 'react';
import { Grid, Typography, Box, Checkbox, Button, Drawer, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Bird, Eye, Pencil } from 'lucide-react';
import styles from './PigeonList.module.css';

const PigeonList = ({ pigeons, onEdit, selectedPigeons = [], onToggleSelect, viewMode = 'tiles', onPedigree }) => {
    const [previewPigeon, setPreviewPigeon] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const flightStatsByPigeon = useMemo(() => {
        const saved = localStorage.getItem('flightsData');
        if (!saved) return {};
        try {
            const flights = JSON.parse(saved);
            if (!Array.isArray(flights)) return {};
            const result = {};
            flights.forEach((flight) => {
                const ring = flight?.ringNumber;
                if (!ring) return;
                if (!result[ring]) {
                    result[ring] = { total: 0, top50: 0, wins: 0, lastFlight: null };
                }
                result[ring].total += 1;
                if ((flight.rankingPosition && Number(flight.rankingPosition) <= 50) || (flight.position && Number(flight.position) <= 50)) {
                    result[ring].top50 += 1;
                }
                if (Number(flight.position) === 1) {
                    result[ring].wins += 1;
                }
                if (!result[ring].lastFlight) {
                    result[ring].lastFlight = flight;
                }
            });
            return result;
        } catch (error) {
            console.error('Blad odczytu statystyk lotow:', error);
            return {};
        }
    }, [pigeons]);

    const getStatusStyle = (status) => {
        if (status === 'Lotowy') return { background: '#e1f5ee', color: '#085041' };
        if (status === 'Rozpłodowy') return { background: '#e6f1fb', color: '#0c447c' };
        return { background: '#f1efe8', color: '#444' };
    };

    const handlePreview = (pigeon) => {
        setPreviewPigeon(pigeon);
        setIsPreviewOpen(true);
    };

    const renderGridCard = (pigeon) => {
        const statusStyle = getStatusStyle(pigeon.status);
        const isSelected = selectedPigeons.includes(pigeon.ID);
        return (
            <Box key={pigeon.ID} sx={{ background:'white', border: isSelected ? '1px solid #2a9d8f' : '0.5px solid #eee', borderRadius: '14px', overflow: 'hidden', cursor: 'pointer', position: 'relative' }}>
                {onToggleSelect && (
                    <Checkbox
                        checked={isSelected}
                        onChange={() => onToggleSelect(pigeon.ID)}
                        sx={{ position: 'absolute', left: 6, top: 6, zIndex: 3, background: 'rgba(255,255,255,0.8)', borderRadius: 1 }}
                    />
                )}
                <Box sx={{ background:'#f0faf7', aspectRatio: '4 / 3', minHeight: 220, display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow: 'hidden' }}>
                    {pigeon.image
                        ? <img src={pigeon.image} alt={pigeon.name} style={{height:'100%', width:'100%', objectFit:'contain'}} />
                        : <Bird size={56} color="#2a9d8f" style={{opacity:0.2}} />
                    }
                    <div style={{position:'absolute', top:8, right:8, display:'flex', flexDirection:'column', gap:6}}>
                        <button onClick={(e) => { e.stopPropagation(); onEdit && onEdit(pigeon); }} style={{width:30, height:30, borderRadius:8, border:'0.5px solid #ddd', background:'white', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer'}}>
                            <Pencil size={13} color="#666" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handlePreview(pigeon); }} style={{width:30, height:30, borderRadius:8, border:'0.5px solid #ddd', background:'white', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer'}}>
                            <Eye size={13} color="#666" />
                        </button>
                    </div>
                </Box>
                <Box sx={{padding: '12px'}}>
                    <div style={{fontSize:14, fontWeight:500, fontFamily:'monospace', marginBottom:2}}>{pigeon.number}</div>
                    <div style={{fontSize:12, color:'#888', marginBottom:10}}>{pigeon.name}</div>
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
                        <span style={{fontSize:12, color:'#666'}}>{pigeon.gender}</span>
                        <span style={{fontSize:11, fontWeight:500, padding:'3px 9px', borderRadius:20, background: statusStyle.background, color: statusStyle.color}}>
                            {pigeon.status}
                        </span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); onPedigree && onPedigree(pigeon.ID); }} style={{width:'100%', background:'transparent', border:'0.5px solid #2a9d8f', color:'#085041', fontSize:12, padding:7, borderRadius:8, fontWeight:500, cursor:'pointer'}}>
                        Rodowód
                    </button>
                </Box>
            </Box>
        );
    };

    const renderListRow = (pigeon) => {
        const statusStyle = getStatusStyle(pigeon.status);
        const isSelected = selectedPigeons.includes(pigeon.ID);
        return (
            <Box key={pigeon.ID} sx={{ background:'white', border: isSelected ? '1px solid #2a9d8f' : '0.5px solid #eee', borderRadius:12, display:'flex', alignItems:'center', gap:14, padding:'12px 16px', mb: 1.4 }}>
                {onToggleSelect && <Checkbox checked={isSelected} onChange={() => onToggleSelect(pigeon.ID)} />}
                <div style={{width:56, height:56, borderRadius:10, background:'#f0faf7', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden'}}>
                    {pigeon.image
                        ? <img src={pigeon.image} alt={pigeon.name} style={{width:56, height:56, objectFit:'cover'}} />
                        : <Bird size={28} color="#2a9d8f" style={{opacity:0.25}} />
                    }
                </div>
                <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:14, fontWeight:500, fontFamily:'monospace'}}>{pigeon.number}</div>
                    <div style={{fontSize:12, color:'#888', marginTop:1}}>{pigeon.name}</div>
                    <div style={{display:'flex', gap:10, marginTop:6, alignItems:'center'}}>
                        <span style={{fontSize:12, color:'#666'}}>{pigeon.gender}</span>
                        <span style={{fontSize:11, fontWeight:500, padding:'3px 9px', borderRadius:20, background: statusStyle.background, color: statusStyle.color}}>
                            {pigeon.status}
                        </span>
                    </div>
                </div>
                <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
                    <button onClick={() => onPedigree && onPedigree(pigeon.ID)} style={{background:'transparent', border:'0.5px solid #2a9d8f', color:'#085041', fontSize:11, padding:'6px 14px', borderRadius:8, fontWeight:500, cursor:'pointer'}}>Rodowód</button>
                    <button onClick={() => handlePreview(pigeon)} style={{width:32, height:32, borderRadius:8, border:'0.5px solid #eee', background:'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer'}}><Eye size={13} color="#666" /></button>
                    <button onClick={() => onEdit && onEdit(pigeon)} style={{width:32, height:32, borderRadius:8, border:'0.5px solid #eee', background:'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer'}}><Pencil size={13} color="#666" /></button>
                </div>
            </Box>
        );
    };

    return (
        <div className={styles['pigeon-container']}>
            {viewMode === 'list' ? (
                <div className={styles['list-view-container']}>
                    {pigeons.map(renderListRow)}
                </div>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 360px))',
                        gap: 2,
                        justifyContent: 'start'
                    }}
                >
                    {pigeons.map((pigeon) => (
                        <Box key={pigeon.ID}>
                            {renderGridCard(pigeon)}
                        </Box>
                    ))}
                </Box>
            )}

            <Drawer
                anchor="right"
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 390 } } }}
            >
                <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 700 }}>Szybki podgląd</Typography>
                    <IconButton onClick={() => setIsPreviewOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                {previewPigeon && (
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{previewPigeon.number}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#666' }}>{previewPigeon.name}</Typography>
                        <Typography sx={{ fontSize: 13 }}>Płeć: {previewPigeon.gender}</Typography>
                        <Typography sx={{ fontSize: 13 }}>Status: {previewPigeon.status}</Typography>
                        <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, background: '#f7fbfa' }}>
                            <Typography sx={{ fontSize: 12 }}>Liczba lotów: {flightStatsByPigeon[previewPigeon.number]?.total || 0}</Typography>
                            <Typography sx={{ fontSize: 12 }}>Top 50: {flightStatsByPigeon[previewPigeon.number]?.top50 || 0}</Typography>
                            <Typography sx={{ fontSize: 12 }}>Zwycięstwa: {flightStatsByPigeon[previewPigeon.number]?.wins || 0}</Typography>
                            <Typography sx={{ fontSize: 12, mt: 0.7 }}>
                                Ostatni lot: {flightStatsByPigeon[previewPigeon.number]?.lastFlight?.name || 'Brak'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                onClick={() => onPedigree && onPedigree(previewPigeon.ID)}
                                sx={{ flex: 1, textTransform: 'none', border: '0.5px solid #2a9d8f', color: '#085041' }}
                            >
                                Zobacz rodowód
                            </Button>
                            <Button
                                onClick={() => onEdit && onEdit(previewPigeon)}
                                sx={{ flex: 1, textTransform: 'none', background: '#2a9d8f', color: '#e1f5ee' }}
                                variant="contained"
                            >
                                Edytuj
                            </Button>
                        </Box>
                    </Box>
                )}
            </Drawer>
        </div>
    );
};

export default PigeonList;
